import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Badge,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { PageContainer } from 'components/shared';
import {
  useGetConversationsQuery,
  useGetConversationQuery,
  useSendMessageMutation,
} from 'state/api';

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('') || '?';

const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatBox = () => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  const { data: listData, isLoading: listLoading } = useGetConversationsQuery({ search });
  const { data: convData, isFetching: convLoading } = useGetConversationQuery(selectedId, {
    skip: !selectedId,
  });
  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();

  const conversations = listData?.data ?? [];
  const conversation = convData?.data ?? null;
  const messages = conversation?.messages ?? [];

  const handleSubmit = async () => {
    const text = newMessage.trim();
    if (!text || !selectedId) return;
    try {
      await sendMessage({ id: selectedId, body: text }).unwrap();
      setNewMessage('');
    } catch (err) {
      // keep input on failure
    }
  };

  return (
    <PageContainer title="Chat" subtitle="View and respond to customer conversations">
      <Grid container spacing={2} sx={{ height: '70vh' }}>
        {/* conversation list */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.alt',
            }}
          >
            <Box p={1.5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {listLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : conversations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" p={4}>
                  No conversations found.
                </Typography>
              ) : (
                <List disablePadding>
                  {conversations.map((c) => (
                    <ListItemButton
                      key={c?._id}
                      selected={selectedId === c?._id}
                      onClick={() => setSelectedId(c?._id)}
                    >
                      <ListItemAvatar>
                        <Badge
                          color="error"
                          badgeContent={c?.unread || 0}
                          invisible={!c?.unread}
                          overlap="circular"
                        >
                          <Avatar src={c?.avatar || undefined}>{getInitials(c?.name)}</Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={c?.name || 'Unknown'}
                        primaryTypographyProps={{
                          noWrap: true,
                          fontWeight: c?.unread ? 'bold' : 'normal',
                        }}
                        secondary={c?.lastMessage || ''}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* thread */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.alt',
            }}
          >
            {!selectedId ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5" color="text.secondary">
                  Select a conversation
                </Typography>
              </Box>
            ) : (
              <>
                <Box p={2}>
                  <Typography variant="h5" fontWeight="bold">
                    {conversation?.name || 'Conversation'}
                  </Typography>
                  {conversation?.subject && (
                    <Typography variant="body2" color="text.secondary">
                      {conversation.subject}
                    </Typography>
                  )}
                </Box>
                <Divider />

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  {convLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : messages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" p={4}>
                      No messages yet.
                    </Typography>
                  ) : (
                    messages.map((m, idx) => {
                      const isAdmin = m?.sender === 'admin';
                      return (
                        <Box
                          key={m?._id || idx}
                          sx={{
                            display: 'flex',
                            justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.25,
                              borderRadius: 2,
                              bgcolor: isAdmin ? 'primary.main' : 'background.secondary',
                              color: isAdmin ? 'primary.contrastText' : 'text.primary',
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold">
                              {m?.senderName || (isAdmin ? 'You' : 'Customer')}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {m?.body || ''}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', opacity: 0.7, mt: 0.5 }}
                            >
                              {formatTime(m?.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Send a message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSubmit}
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? <CircularProgress size={20} /> : <Send />}
                  </IconButton>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default ChatBox;
